import React, { useMemo } from 'react';
import {
    LOST_STAGE,
    dealValue,
    daysInStage,
    formatEurShort,
    funnelStats,
    stageClass,
} from '../utils/salesPipeline';

/** The pipeline as one segmented bar: width is how many deals reached a stage, the number between
 *  two is the conversion, and a click filters the list below. */
export default function SalesFunnel({ acquisitions = [], activeStage = '', onStageClick }) {
    const { stages, lost, total } = useMemo(() => {
        const computed = funnelStats(acquisitions).map(stage => {
            const inStage = acquisitions.filter(a => a.status === stage.key);
            const ages = inStage.map(a => daysInStage(a)).filter(d => d !== null);
            return {
                ...stage,
                avgDays: ages.length ? Math.round(ages.reduce((s, d) => s + d, 0) / ages.length) : null,
            };
        });
        const lostDeals = acquisitions.filter(a => a.status === LOST_STAGE);
        return {
            stages: computed,
            lost: {
                count: lostDeals.length,
                value: lostDeals.reduce((sum, a) => sum + dealValue(a), 0),
            },
            total: acquisitions.length,
        };
    }, [acquisitions]);

    const tooltip = (stage) => [
        `${stage.label}: ${stage.count} Deal${stage.count === 1 ? '' : 's'} · ${formatEurShort(stage.value)}`,
        stage.avgDays === null ? null : `Ø ${stage.avgDays} Tage in Phase`,
        `${stage.reached} Deals haben diese Phase erreicht`,
        stage.hint,
    ].filter(Boolean).join('\n');

    return (
        <section className="funnel">
            <div className="funnel-head">
                <h3 className="funnel-title">Vertriebsprozess</h3>
                <span className="funnel-sub">
                    Breite = erreichte Deals · % = Übergang zur nächsten Phase · Klick filtert
                </span>
            </div>

            <div className="funnel-rail">
                {stages.map((stage, idx) => (
                    <React.Fragment key={stage.key}>
                        {idx > 0 && (
                            <span className="funnel-conv" title={`Übergang ${stages[idx - 1].label} → ${stage.label}`}>
                                {stage.conversion === null ? '—' : `${stage.conversion}%`}
                            </span>
                        )}
                        <button
                            type="button"
                            className={`funnel-seg${activeStage === stage.key ? ' is-active' : ''}${stage.count === 0 ? ' is-empty' : ''}`}
                            style={{ flexGrow: Math.max(1, stage.reached) }}
                            onClick={() => onStageClick?.(activeStage === stage.key ? '' : stage.key)}
                            title={tooltip(stage)}
                        >
                            <span className={`funnel-seg-bar ${stageClass(stage.key)}`} />
                            <span className="funnel-seg-label">
                                {stage.short}<strong>{stage.count}</strong>
                            </span>
                        </button>
                    </React.Fragment>
                ))}

                <span className="funnel-split" aria-hidden="true" />

                <button
                    type="button"
                    className={`funnel-seg funnel-seg--lost${activeStage === LOST_STAGE ? ' is-active' : ''}${lost.count === 0 ? ' is-empty' : ''}`}
                    onClick={() => onStageClick?.(activeStage === LOST_STAGE ? '' : LOST_STAGE)}
                    title={`Verloren: ${lost.count} Deals · ${formatEurShort(lost.value)}\nAus dem Trichter ausgestiegen`}
                >
                    <span className={`funnel-seg-bar ${stageClass(LOST_STAGE)}`} />
                    <span className="funnel-seg-label">
                        Verl.<strong>{lost.count}</strong>
                    </span>
                </button>
            </div>

            {total === 0 && <p className="funnel-empty">Noch keine Deals in der Pipeline.</p>}
        </section>
    );
}
